/**
 * ComingSoon — the themed placeholder for a not-yet-built screen.
 *
 * Consistent with the MUI shell: a centered `Paper` card using the theme card
 * radius/shadow, the screen's named Material Symbols glyph, the screen title,
 * and a short "coming soon" note. Presentation only — no data, no API calls.
 */
import { Box, Paper, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { Icon } from '@/theme';

export interface ComingSoonProps {
  /** Screen title (e.g. "Dashboards"). */
  title: string;
  /** Material Symbols Outlined glyph name for the screen (e.g. `dashboard`). */
  icon: string;
  /** Optional one-line description of what will live here. */
  description?: string;
}

export function ComingSoon({ title, icon, description }: ComingSoonProps) {
  const { t } = useCopy();
  const body =
    description ??
    t('common.comingSoonBody', 'The {{title}} screen is part of a later release.', { title });
  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 2.5 }}>
        {title}
      </Typography>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 1.5,
          px: 4,
          py: 7,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            borderRadius: 2,
            bgcolor: 'background.default',
            color: 'primary.main',
          }}
        >
          <Icon name={icon} sx={{ fontSize: 30 }} />
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
          {t('common.comingSoon', 'Coming soon')}
        </Typography>
        <Typography sx={{ color: 'text.secondary', maxWidth: 420 }}>{body}</Typography>
      </Paper>
    </Box>
  );
}

export default ComingSoon;
